/**
 * Products Service
 * Handles all product-related operations
 */

const BASE_URL = process.env.NODE_ENV === 'production' 
  ? (process.env.REACT_APP_API_URL || '/api')
  : (process.env.REACT_APP_API_URL || 'http://localhost:5000/api');

const toUploadsPath = (url) => {
  if (!url || typeof url !== 'string') return url;

  const trimmedUrl = url.trim();
  if (!trimmedUrl) return trimmedUrl;

  if (trimmedUrl.startsWith('/uploads/')) {
    return trimmedUrl;
  }

  if (trimmedUrl.startsWith('uploads/')) {
    return `/${trimmedUrl}`;
  }

  try {
    const parsedUrl = new URL(trimmedUrl);
    if (parsedUrl.pathname.startsWith('/uploads/')) {
      return parsedUrl.pathname;
    }
  } catch (_error) {
    // Not an absolute URL, continue with original value
  }

  return trimmedUrl;
};

const upgradeInsecureHttpUrl = (url) => {
  if (!url || typeof url !== 'string') return url;

  const trimmedUrl = url.trim();
  if (!trimmedUrl.startsWith('http://')) {
    return trimmedUrl;
  }

  const isSecurePage = typeof window !== 'undefined' && window.location.protocol === 'https:';
  if (!isSecurePage) {
    return trimmedUrl;
  }

  try {
    const parsedUrl = new URL(trimmedUrl);
    const isLocalHost = parsedUrl.hostname === 'localhost'
      || parsedUrl.hostname === '127.0.0.1'
      || parsedUrl.hostname === '0.0.0.0';

    if (isLocalHost) {
      return trimmedUrl;
    }

    parsedUrl.protocol = 'https:';
    return parsedUrl.toString();
  } catch (_error) {
    return trimmedUrl;
  }
};

// Helper function to normalize image URLs
const normalizeSingleImageUrl = (url) => {
  if (!url) return url;

  const uploadsPath = toUploadsPath(url);
  if (uploadsPath && uploadsPath.startsWith('/uploads/')) {
    return `${BASE_URL.replace('/api', '')}${uploadsPath}`;
  }

  return upgradeInsecureHttpUrl(url);
};

const parseImages = (product) => {
  // Prefer explicit images array from API when present
  if (Array.isArray(product.images) && product.images.length > 0) {
    return product.images;
  }

  if (typeof product.images === 'string') {
    try {
      const parsedImages = JSON.parse(product.images);
      if (Array.isArray(parsedImages)) {
        return parsedImages;
      }
    } catch (_error) {
      // Fall through to image_url parsing
    }
  }

  // The server stores images in image_url: either a plain URL or a JSON array string
  if (typeof product.image_url === 'string' && product.image_url.trim().startsWith('[')) {
    try {
      const parsed = JSON.parse(product.image_url.trim());
      if (Array.isArray(parsed)) {
        return parsed.filter((url) => typeof url === 'string' && url.trim());
      }
    } catch (_error) {
      // Fall through
    }
  }

  if (product.image_url) {
    return [product.image_url];
  }

  return [];
};

const normalizeProductImages = (product) => {
  const parsedImages = parseImages(product)
    .filter((imageUrl) => typeof imageUrl === 'string' && imageUrl.trim())
    .map((imageUrl) => imageUrl.trim());

  const images = parsedImages.length > 0
    ? parsedImages
    : [];

  const normalizedImages = images
    .map((imageUrl) => normalizeSingleImageUrl(imageUrl))
    .filter(Boolean);

  return {
    ...product,
    image_url: normalizedImages[0] || normalizeSingleImageUrl(product.image_url) || null,
    images: normalizedImages,
  };
};

/**
 * Get all products with optional filtering
 */
export const getProducts = async (category = null, subcategory = null) => {
  try {
    let url = `${BASE_URL}/products`;
    const params = new URLSearchParams();
    
    if (category) params.append('category', category);
    if (subcategory) params.append('subcategory', subcategory);
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch products: ${response.status}`);
    }

    const products = await response.json();
    
    // Normalize image URLs
    return products.map(product => {
      const normalizedProduct = normalizeProductImages(product);
      return {
        ...normalizedProduct,
        discounted_price: product.discount_percent > 0 
          ? Number((product.price - (product.price * (product.discount_percent / 100))).toFixed(2))
          : null,
      };
    });
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

/**
 * Get a single product by ID
 */
export const getProductById = async (id) => {
  try {
    const response = await fetch(`${BASE_URL}/products/${id}`);
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Product not found');
      }
      throw new Error(`Failed to fetch product: ${response.status}`);
    }

    const product = normalizeProductImages(await response.json());
    
    if (product.discount_percent > 0) {
      product.discounted_price = Number((product.price - (product.price * (product.discount_percent / 100))).toFixed(2));
    }
    
    return product;
  } catch (error) {
    console.error('Error fetching product:', error);
    throw error;
  }
};

/**
 * Get products by category
 */
export const getProductsByCategory = async (category) => {
  try {
    const response = await fetch(`${BASE_URL}/products/category/${encodeURIComponent(category)}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch products: ${response.status}`);
    }

    const products = await response.json();
    return products.map(product => normalizeProductImages(product));
  } catch (error) {
    console.error('Error fetching products by category:', error);
    throw error;
  }
};

const productServiceExports = {
  getProducts,
  getProductById,
  getProductsByCategory,
};

export default productServiceExports;
